import {
    createSlice,
    PayloadAction,
    Draft,
    ActionReducerMapBuilder,
    Action,
} from "@reduxjs/toolkit";
import { newActivity } from "../../../../../../redux";
import { AppDispatch, GetRootState } from "../../../../../../../../shared/redux/types";
import { Data, DataResponse } from "../../../../../../../../services/dataEntering/data/types";
import { fetchData } from "../../../../../../../../services/dataEntering/data/api";

export interface FormState {
    errors: string[];
    currentStep: number;
    error: string | null;
    status:
        | "uninitialized"
        | "initialized"
        | "initializing"
        | "loading"
        | "succeeded"
        | "failed"
        | "error";
    /* eslint-disable  @typescript-eslint/no-explicit-any */
    data: Record<string, any>;
}

interface UpdateActionPayload {
    /* eslint-disable  @typescript-eslint/no-explicit-any */
    [key: string]: any;
}

export const initialFormState: FormState = {
    errors: [],
    currentStep: 1,
    error: null,
    status: "uninitialized",
    data: {},
};

function isNewActivity(action: Action): boolean {
    return newActivity.match(action);
}

export function createFormSlice(
    name: string,
    extraReducers: (builder: ActionReducerMapBuilder<Draft<FormState>>) => void,
    initialState: FormState = initialFormState,
) {
    return createSlice({
        name,
        initialState,
        reducers: {
            update(draftState: Draft<FormState>, action: PayloadAction<UpdateActionPayload>) {
                draftState.data = { ...draftState.data, ...action.payload };
            },
            nextStep(draftState: Draft<FormState>) {
                draftState.currentStep += 1;
            },
            prevStep(draftState: Draft<FormState>) {
                draftState.currentStep -= 1;
            },
            setStep(draftState: Draft<FormState>, action: PayloadAction<number>) {
                draftState.currentStep = action.payload;
            },
            updateErrors(draftState: Draft<FormState>, action: PayloadAction<string[]>) {
                draftState.errors = action.payload;
            },
            clearErrors(draftState: Draft<FormState>) {
                draftState.errors = [];
            },
            reset(): FormState {
                return initialState;
            },
        },
        extraReducers: (builder) => {
            extraReducers(builder);

            // reset form on new activity
            builder.addMatcher(isNewActivity, (): FormState => initialState);
            builder.addDefaultCase(() => {});
        },
    });
}

const defaultData: Data = {
    category: "uncategorized",
    note: "",
    currencyCode: "",
    invoiceNumber: "",
    issueDate: "",
    taxPaymentDate: "",
    supplier: null,
    customer: null,
    counterparty: null,
    amounts: null,
    payment: null,
    items: [],
    documentType: "standard_invoice",
};

const defaultDataResponse: DataResponse = {
    data: defaultData,
    version: "0.0",
};

export async function fetchLatestDataOrDefault(
    dispatch: AppDispatch,
    getState: GetRootState,
): Promise<DataResponse> {
    const { dataEntering } = getState();
    const { accountId, documentId } = dataEntering;
    const { data, isError } = await fetchData({ dispatch, accountId, documentId });

    return isError ? defaultDataResponse : data;
}
